import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldCheck, X } from "lucide-react";
import { NeuButton } from "../../components/neu/Neu";
import { useAuth } from "../../context/AuthContext";
import { isAdmin as isAdminRole } from "../../utils/role";
import { getUserPerms, setUserPerms, deleteCustomPerm } from "../../api/permissions";
import { usePermsCatalog } from "../../hooks/usePermsCatalog";
import { PermsModuleGroup } from "./PermsModuleGroup";
import { AddCustomPermForm } from "./AddCustomPermForm";

// Modal شامل: كتالوج الصلاحيات + تحديد + إضافة مخصّص — يفتح من أي مكان بالتطبيق.
export const PermissionsModal = ({ userId, userName, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = isAdminRole(user);
  const { modules, count, reload } = usePermsCatalog();
  const [selected, setSelected] = useState([]);
  const [targetIsAdmin, setTargetIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const d = await getUserPerms(userId);
    setSelected(d.permissions || []);
    setTargetIsAdmin(!!d.is_admin);
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const moduleKeys = useMemo(() => Object.keys(modules).sort(), [modules]);
  const toggle = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  const bulk = (mod, turnOn) => {
    const keys = (modules[mod] || []).map((it) => it.key);
    setSelected((s) => (turnOn ? Array.from(new Set([...s, ...keys])) : s.filter((k) => !keys.includes(k))));
  };
  const removeCustom = async (key) => {
    await deleteCustomPerm(key);
    toast.success(t("perms.customRemoved"));
    reload();
  };
  const save = async () => {
    setBusy(true);
    try {
      await setUserPerms(userId, selected);
      toast.success(t("perms.saved"));
      onSaved && onSaved();
      onClose();
    } catch { toast.error(t("errors.generic")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 p-2 sm:items-center">
      <div className="neu flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden" data-testid="permissions-modal">
        <div className="flex items-center justify-between gap-3 border-b border-border/40 p-4">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/15 text-primary shadow-neu-in"><ShieldCheck className="h-5 w-5" /></div>
            <div>
              <p className="text-base font-black">{t("perms.title")}</p>
              <p className="text-xs text-muted-foreground">{userName || userId} · {t("perms.available", { count })}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-2xl p-2 hover:bg-muted/50" data-testid="perms-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="grid gap-4 overflow-y-auto p-4">
          {targetIsAdmin ? (
            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 text-sm">{t("perms.adminHint")}</div>
          ) : null}
          {isAdmin ? <AddCustomPermForm modules={moduleKeys} onAdded={reload} /> : null}
          {moduleKeys.map((m) => (
            <PermsModuleGroup
              key={m} module={m} items={modules[m]}
              selected={selected} onToggle={toggle} onBulk={bulk}
              canDelete={isAdmin} onDeleteCustom={removeCustom}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border/40 p-4">
          <span className="text-xs text-muted-foreground">{t("perms.selectedCount", { n: selected.length })}</span>
          <div className="flex gap-2">
            <NeuButton type="button" data-testid="perms-cancel" onClick={onClose}>{t("app.cancel")}</NeuButton>
            <NeuButton variant="primary" type="button" data-testid="perms-save" disabled={busy || !isAdmin} onClick={save}>{t("app.save")}</NeuButton>
          </div>
        </div>
      </div>
    </div>
  );
};
